import { API } from "@swivl-mono/api-helper";
import { PulsePageSettingsUpdate, PulseSettingsView } from "@swivl-mono/types";
import { Select, SelectSize } from "@swivl-mono/ui";
import { ReactNode, useEffect, useState } from "react";

import { SavedIndicator, useSavedIndicator } from "../../../../../Components/SavedIndicator/SavedIndicator";
import { Member } from "../../../../../Models/Member/Member.Current";
import { AlertsModel } from "../../../../../Models/Notifications/Alerts.Model";
import { WidgetSwitch } from "../../../../../Views/Widgets/WidgetSwitch";
import { DEFAULT_EMAIL_HOUR, FRESHNESS_DEFAULT, HOUR_OPTIONS, freshnessOptions, lastSentLabel, parseFreshness, zoneLabel } from "./PulseTab.Helpers";

/**
 * Account settings > Pulse (ENG-3222 decisions 8-12): the emailed page, its
 * hour and the zone it's read in, and how fresh the page stays. Each change
 * saves at once, like the Notifications tab.
 */
export function PulseTab(): ReactNode {
  const [view, setView] = useState<PulseSettingsView | null>(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const saved = useSavedIndicator();
  const orgId = Member.orgId;

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const next = await API.Pulse.getPageSettings(orgId);
        if (!cancelled) setView(next);
      } catch {
        if (!cancelled) setFailed(true);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  async function save(update: PulsePageSettingsUpdate): Promise<void> {
    setSaving(true);
    try {
      const settings = await API.Pulse.setPageSettings(orgId, update);
      setView((current) => (current ? { ...current, email: settings.email ?? null, freshnessHours: settings.freshnessHours ?? null } : current));
      saved.flash();
    } catch {
      AlertsModel.errorToast("Couldn't save your Pulse settings. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  function handleEmailToggle(on: boolean): void {
    void save({ email: { on, hour: view?.email?.hour ?? DEFAULT_EMAIL_HOUR } });
  }

  function handleHourChange(values: string[]): void {
    const hour = Number(values[0]);
    if (!Number.isInteger(hour)) return;
    void save({ email: { on: view?.email?.on ?? false, hour } });
  }

  function handleFreshnessChange(values: string[]): void {
    void save({ freshnessHours: parseFreshness(values[0]) });
  }

  if (failed) return <p className="body-sm text-grey-600">Your Pulse settings couldn't load. Close this and try again.</p>;
  if (!view) return <p className="body-sm text-grey-600">Loading…</p>;
  if (!view.persona) return <p className="body-sm text-grey-600">Pulse needs a persona first. Ask your admin to set one on the Members page.</p>;

  const emailOn = view.email?.on ?? false;
  const zone = view.email?.zone ?? view.zone?.zone ?? null;
  const zoneSource = view.zone?.source === "locations" ? "your locations' time zone" : "your organization's time zone";
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-end">
        <SavedIndicator visible={saved.show} />
      </div>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col">
            <span className="body-sm-alt text-black">Email me my page</span>
            <span className="body-sm text-grey-600">
              {view.cadence === "weekly" ? "Your Pulse, every Monday." : "Your Pulse, every day."} Rewritten first only when something changed.
            </span>
          </div>
          <WidgetSwitch on={emailOn} onSet={handleEmailToggle} ariaLabel="Email me my page" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="body-sm text-grey-600">{view.cadence === "weekly" ? "Mondays at" : "Every day at"}</span>
          <div className="w-32">
            <Select
              options={HOUR_OPTIONS}
              selectedValues={[String(view.email?.hour ?? DEFAULT_EMAIL_HOUR)]}
              onChange={handleHourChange}
              size={SelectSize.SMALL}
              disabled={!emailOn || saving}
              hideClear
            />
          </div>
          {zone && (
            <span className="body-sm text-grey-600">
              {zoneLabel(zone)}, {zoneSource}
            </span>
          )}
        </div>
        <span className="body-sm text-grey-600">Sent to {view.address ?? "your notification email"}. You can change it under Notifications.</span>
        {emailOn && !view.canSend && (
          <span className="body-sm text-orange-500">Your organization doesn't send email yet, so nothing will arrive until Swivl turns it on.</span>
        )}
        {view.lastEmailedOn && <span className="body-sm text-grey-600">{lastSentLabel(view.lastEmailedOn, view.cadence)}</span>}
      </section>

      <section className="flex flex-col gap-2">
        <span className="body-sm-alt text-black">Keep my page fresh</span>
        <span className="body-sm text-grey-600">When you've opened Pulse this week, it rewrites your page once it's older than this.</span>
        <div className="w-64">
          <Select
            options={freshnessOptions(view.personaFreshnessHours)}
            selectedValues={[view.freshnessHours ? String(view.freshnessHours) : FRESHNESS_DEFAULT]}
            onChange={handleFreshnessChange}
            size={SelectSize.SMALL}
            disabled={saving}
            hideClear
          />
        </div>
      </section>
    </div>
  );
}
